import { useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import type { CostClassification, UsageCostBreakdown } from '../../types';

interface Props {
  breakdown: UsageCostBreakdown | null;
  classification: CostClassification;
  serviceDisplayNames: Record<string, string>;
}

interface CostItem {
  name: string;
  cost: number;
}

interface CategorizedCosts {
  fixedTotal: number;
  variableTotal: number;
  fixedItems: CostItem[];
  variableItems: CostItem[];
}

function categorize(
  breakdown: UsageCostBreakdown | null,
  classification: CostClassification,
  displayNames: Record<string, string>
): CategorizedCosts {
  if (!breakdown) {
    return { fixedTotal: 0, variableTotal: 0, fixedItems: [], variableItems: [] };
  }

  let fixedTotal = 0;
  let variableTotal = 0;
  const fixedRaw: CostItem[] = [];
  const variableRaw: CostItem[] = [];
  const fixedSet = new Set(classification.fixed_service_ids);

  for (const [serviceId, cost] of Object.entries(breakdown.service_totals)) {
    const name = displayNames[serviceId] ?? serviceId;
    if (fixedSet.has(serviceId)) {
      fixedTotal += cost;
      if (cost > 0.01) fixedRaw.push({ name, cost });
    } else {
      variableTotal += cost;
      if (cost > 0.01) variableRaw.push({ name, cost });
    }
  }

  fixedRaw.sort((a, b) => b.cost - a.cost);
  variableRaw.sort((a, b) => b.cost - a.cost);

  const fixedCutoff = fixedRaw.length > 0 ? fixedRaw[0].cost * 0.05 : 0;
  const variableCutoff = variableRaw.length > 0 ? variableRaw[0].cost * 0.05 : 0;

  return {
    fixedTotal,
    variableTotal,
    fixedItems: fixedRaw.filter((item) => item.cost >= fixedCutoff),
    variableItems: variableRaw.filter((item) => item.cost >= variableCutoff),
  };
}

export function CostKeyContributors({
  breakdown,
  classification,
  serviceDisplayNames,
}: Props) {
  const { fixedTotal, variableTotal, fixedItems, variableItems } = useMemo(
    () => categorize(breakdown, classification, serviceDisplayNames),
    [breakdown, classification, serviceDisplayNames]
  );

  if (!breakdown) return null;

  const total = breakdown.total_monthly;
  const fixedPct = total > 0 ? (fixedTotal / total) * 100 : 0;
  const variablePct = total > 0 ? (variableTotal / total) * 100 : 0;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Fixed Costs
          </Typography>
          <Typography variant="h5" fontWeight={600} color="warning.main">
            ${fixedTotal.toFixed(2)}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              ({fixedPct.toFixed(0)}%)
            </Typography>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            Infrastructure baseline
          </Typography>
          <Box sx={{ mt: 1 }}>
            {fixedItems.map((item) => (
              <Box
                key={item.name}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 0.5,
                }}
              >
                <Typography variant="body2">{item.name}</Typography>
                <Typography variant="body2" fontWeight={500}>
                  ${item.cost.toFixed(2)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Variable Costs
          </Typography>
          <Typography variant="h5" fontWeight={600} color="success.main">
            ${variableTotal.toFixed(2)}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              ({variablePct.toFixed(0)}%)
            </Typography>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            Scales with user activity
          </Typography>
          <Box sx={{ mt: 1 }}>
            {variableItems.map((item) => (
              <Box
                key={item.name}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 0.5,
                }}
              >
                <Typography variant="body2">{item.name}</Typography>
                <Typography variant="body2" fontWeight={500}>
                  ${item.cost.toFixed(2)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
